import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Star, Sparkles, Download } from "lucide-react";
import { useAuth, getRefreshToken, setTokens, clearTokens } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { useCurrency } from "../contexts/CurrencyContext";
import PaywallModal from "../components/PaywallModal";
import AstroMarkdown from "../components/AstroMarkdown";
import InlineBirthForm from "../components/InlineBirthForm";
import ChartSummaryCard from "../components/ChartSummaryCard";
import AccuracyVote from "../components/AccuracyVote";
import { KundliPdfActions } from "../components/KundliPdfActions";
import { downloadKundliPdf } from "../lib/kundliPdf";
import { claimLocalReport, signupUrl } from "../lib/claimReport";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CURRENT_YEAR = new Date().getFullYear();
const SUGGESTED_PROMPTS_EN = [
  "When will I get married?",
  "Career growth in next 2 years?",
  `Financial stability in ${CURRENT_YEAR}?`,
  "Foreign settlement chances?",
  "Relationship compatibility?",
  "When will I have children?",
  "Best time to start a business?",
];
const SUGGESTED_PROMPTS_HI = [
  "मेरी शादी कब होगी?",
  "अगले 2 साल में करियर वृद्धि?",
  `${CURRENT_YEAR} में आर्थिक स्थिरता?`,
  "विदेश settlement के chances?",
  "रिश्ते की compatibility?",
  "मेरे बच्चे कब होंगे?",
  "बिज़नेस शुरू करने का best समय?",
];

function TypingIndicator() {
  return (
    <div style={{ display: "flex", gap: "4px", alignItems: "center", padding: "12px 16px" }}>
      {["dot-a","dot-b","dot-c"].map(k => (
        <div key={k} className="typing-dot" style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#9D4EDD" }} />
      ))}
    </div>
  );
}

export default function ChatPage() {
  const { user, refreshUser } = useAuth();
  const { t, lang } = useI18n();
  const { price, priceOld } = useCurrency();
  const SUGGESTED_PROMPTS = lang === "hi" ? SUGGESTED_PROMPTS_HI : SUGGESTED_PROMPTS_EN;
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("ask") || "";
  });
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [freeUsed, setFreeUsed] = useState(false);
  const [bonusQuestions, setBonusQuestions] = useState(0);
  const [chartJson, setChartJson] = useState(null);
  const [reportName, setReportName] = useState("");
  const [questionsRemaining, setQuestionsRemaining] = useState(null);
  const [charts, setCharts] = useState([]);
  const [activeChartId, setActiveChartId] = useState(null);
  const [chartChosen, setChartChosen] = useState(false);
  const [chartLoading, setChartLoading] = useState(true);
  const [showBirthForm, setShowBirthForm] = useState(false);
  const [chartSummary, setChartSummary] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [hasFullReport, setHasFullReport] = useState(() => {
    try { return !!JSON.parse(localStorage.getItem("astrofate_report") || "null")?.report; } catch { return false; }
  });
  const autoSentRef = useRef(false);
  const authLoading = user === undefined;
  const bottomRef = useRef(null);
  const lastMsgRef = useRef(null);
  const inputRef = useRef(null);
  const [showGlance, setShowGlance] = useState(false);
  const [teaserLoading, setTeaserLoading] = useState(false);

  useEffect(() => {
    if (!messages.length && !loading) return;
    const last = messages[messages.length - 1];
    // A fresh answer is scrolled to its *start* — scrolling to the very bottom used
    // to park a long reading above the fold behind the upgrade bar.
    if (!loading && last?.role === "ai" && lastMsgRef.current) {
      lastMsgRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, loading]);

  // Kundli gate. The chart shown in "your chart at a glance" must be the very
  // same chart the answers are read from, so `activeChartId` + `chartJson` are
  // always loaded as a pair. A cached localStorage report is only trusted when it
  // still exists in the user's saved list (i.e. it is the one opened this session);
  // otherwise we fall back to the latest saved Kundli or ask the user to pick.
  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    let local = null;
    try {
      const stored = localStorage.getItem("astrofate_report");
      if (stored) local = JSON.parse(stored);
    } catch { /* corrupt cache */ }

    if (!user) {
      if (local?.chart) {
        setChartJson(local.chart);
        setReportName(local.birth_details?.name || local.name || "");
      }
      setChartLoading(false);
      return;
    }

    const loadChart = async (id) => {
      const c = await axios.get(`${API}/kundli/chart/${id}`, { withCredentials: true });
      if (cancelled) return;
      setChartJson(c.data?.chart || null);
      setReportName(c.data?.name || "");
    };

    (async () => {
      // A Kundli made before sign-up is attached to the new account first.
      if (local?.chart && !local.report_id && !local.id) {
        local = (await claimLocalReport()) || local;
        if (cancelled) return;
      }
      try {
        const res = await axios.get(`${API}/kundli/charts`, { withCredentials: true });
        const list = res.data?.charts || [];
        if (cancelled) return;
        setCharts(list);

        const localId = local?.report_id || local?.id || null;
        let cached = localId ? list.find(c => c.id === localId) : null;
        // The list only carries the most recent Kundlis, so a valid older report
        // opened from the dashboard is confirmed straight against the API.
        if (localId && !cached) {
          try {
            const c = await axios.get(`${API}/kundli/chart/${localId}`, { withCredentials: true });
            if (cancelled) return;
            cached = { id: localId, name: c.data?.name || "" };
          } catch { cached = null; }
        }

        if (cached) {
          setActiveChartId(cached.id);
          setChartChosen(true);
          if (local.chart) {
            setChartJson(local.chart);
            setReportName(local.birth_details?.name || local.name || cached.name || "");
          } else {
            await loadChart(cached.id);
          }
        } else if (list.length === 1) {
          setActiveChartId(list[0].id);
          setChartChosen(true);
          await loadChart(list[0].id);
        } else if (list.length > 1) {
          // Stale cache + several Kundlis: never guess. Drop the cached chart and ask.
          setChartJson(null);
          setReportName("");
          setActiveChartId(null);
          setChartChosen(false);
        } else if (local?.chart) {
          setChartJson(local.chart);
          setReportName(local.birth_details?.name || local.name || "");
        }
      } catch {
        if (!cancelled && local?.chart) {
          setChartJson(local.chart);
          setReportName(local.birth_details?.name || local.name || "");
        }
      }
      if (!cancelled) setChartLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  // "Your chart at a glance" — Lagna / Sun / Moon / Nakshatra / current Dasha
  useEffect(() => {
    if (!chartJson) { setChartSummary(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${API}/kundli/summary`,
          (user && activeChartId) ? { report_id: activeChartId } : { chart_json: chartJson },
          { withCredentials: true }
        );
        if (!cancelled) setChartSummary(res.data?.summary || null);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [user, chartJson, activeChartId]);

  const switchChart = async (id) => {
    setActiveChartId(id);
    setChartChosen(true);
    setChartLoading(true);
    try {
      const c = await axios.get(`${API}/kundli/chart/${id}`, { withCredentials: true });
      setChartJson(c.data?.chart || null);
      setReportName(c.data?.name || "");
      setShowBirthForm(false);
    } catch {} finally { setChartLoading(false); }
  };

  const handleChartConnected = async ({ chart, name, report_id, report_md }) => {
    setChartJson(chart);
    setReportName(name || "");
    setShowBirthForm(false);
    setChartChosen(true);
    try { setHasFullReport(!!JSON.parse(localStorage.getItem("astrofate_report") || "null")?.report); } catch {}
    if (user) {
      try {
        const res = await axios.get(`${API}/kundli/charts`, { withCredentials: true });
        const list = res.data?.charts || [];
        setCharts(list);
        // The chart just generated is the one we answer from — pin it explicitly.
        setActiveChartId(report_id || list[0]?.id || null);
      } catch {}
    }
    setMessages(prev => [...prev, { role: "ai", id: Date.now(), isChartConnected: true, text: user ? t("chart.connectedMsg") : t("chart.readyGuest") }]);
    // Land straight on the question box — the chart step is done.
    setTimeout(() => inputRef.current?.focus(), 250);
    // A real 60-word read straight away: value before any signup ask.
    setTeaserLoading(true);
    axios.post(`${API}/kundli/teaser`, { chart_json: chart, report_id: report_id || undefined, report_md: report_md || undefined, lang }, { withCredentials: true })
      .then(r => {
        if (r.data?.teaser) setMessages(prev => [...prev, { role: "ai", id: Date.now() + 1, isTeaser: true, text: r.data.teaser }]);
      })
      .catch(() => {})
      .finally(() => setTeaserLoading(false));
    // Then the read people actually want: nature + how the last months have felt,
    // cross-checked across the chart. Free, and it costs them no question.
    axios.post(`${API}/kundli/first-read-chat`, { chart_json: chart, report_id: report_id || undefined, lang }, { withCredentials: true })
      .then(r => {
        if (r.data?.answer) setMessages(prev => [...prev, { role: "ai", id: Date.now() + 2, isDeepRead: true, text: r.data.answer }]);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (user) {
      const bonus = user.bonus_questions || 0;
      setBonusQuestions(bonus);
      setFreeUsed((user.free_question_used || false) && bonus <= 0);
      if (user.plan === "ai_chat") {
        const remaining = Math.max(0, (user.ai_chat_max || 4) - (user.ai_chat_questions_used || 0));
        setQuestionsRemaining(remaining);
      } else if (user.plan === "consultation" || user.plan === "admin") {
        setQuestionsRemaining(null); // unlimited
      }
    }
  }, [user]);

  const isPremium = user && ["ai_chat", "consultation", "admin"].includes(user.plan);
  const isAiChatExhausted = user?.plan === "ai_chat" && questionsRemaining === 0;
  // Once a real answer is on screen the page steps back: chart chips and helper
  // panels hide so the reading itself gets the room.
  const hasAnswer = messages.some(m => m.role === "ai" && !m.isChartGate && !m.isChartConnected && !m.isChooseChart);
  // Several saved Kundlis and none picked yet → ask which chart to read first.
  const showChooser = !!user && charts.length > 1 && !chartChosen && !chartLoading;

  const handleSend = async (text = input) => {
    if (!text.trim() || loading) return;
    // Kundli gate — no chart, no answer. Ask for DOB details first (works without login).
    if (!chartJson && (!user || charts.length <= 1)) {
      setShowBirthForm(true);
      setMessages(prev => [
        ...prev,
        { role: "user", text: text.trim(), id: Date.now() },
        { role: "ai", id: Date.now() + 1, isChartGate: true, text: t("chart.needDetails") },
      ]);
      setInput("");
      return;
    }
    if (!user) {
      // Chart is ready — sign-up is the only step left. The question travels along
      // and is sent automatically once the account exists.
      navigate(signupUrl(`/chat?ask=${encodeURIComponent(text.trim())}&autosend=1`));
      return;
    }

    // Multiple Kundlis saved and none picked yet — ask which one to use first.
    if (charts.length > 1 && !chartChosen) {
      setMessages(prev => [
        ...prev,
        { role: "user", text: text.trim(), id: Date.now() },
        { role: "ai", id: Date.now() + 1, isChooseChart: true, text: t("chart.choosePrompt") },
      ]);
      setInput("");
      return;
    }

    // Kundli gate — no chart, no answer. Ask for DOB details first.
    if (!chartJson) {
      setShowBirthForm(true);
      setMessages(prev => [
        ...prev,
        { role: "user", text: text.trim(), id: Date.now() },
        { role: "ai", id: Date.now() + 1, isChartGate: true, text: t("chart.needDetails") },
      ]);
      setInput("");
      return;
    }

    // Free user who has used their free question -> paywall
    if (freeUsed && !isPremium) { setShowPaywall(true); return; }
    // AI Chat plan user who has hit the 4-question cap -> astrologer upsell
    if (isAiChatExhausted) {
      setMessages(prev => [...prev, {
        role: "ai",
        id: Date.now(),
        isAstrologerCTA: true,
        text: `You've used all 4 questions in your AI Chat plan. For deeper, in-person guidance, book a 15-minute consultation with our certified Vedic astrologer for just ${price("consultation")} (50% OFF, limited time).`
      }]);
      return;
    }

    const userMsg = { role: "user", text: text.trim(), id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      let endpoint = isPremium ? "/chat/message" : "/chat/free-question";
      const payload = {
        question: text.trim(),
        session_id: sessionId,
        // One source of truth: when a saved Kundli is selected the backend reads
        // that exact chart, so the answer can never come from a different chart
        // than the one shown in "your chart at a glance".
        ...(activeChartId ? { report_id: activeChartId } : { chart_json: chartJson }),
        lang,                    // 'en' | 'hi' — backend prepends a language instruction
      };
      let res;
      try {
        res = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      } catch (firstErr) {
        if (firstErr.response?.status === 401) {
          try {
            // Try refresh — send the refresh token in body for environments where cookies don't work
            const refreshRes = await axios.post(
              `${API}/auth/refresh`,
              { refresh_token: getRefreshToken() },
              { withCredentials: true }
            );
            // Update stored access token if returned
            if (refreshRes.data?.access_token) {
              setTokens(refreshRes.data.access_token, getRefreshToken());
            }
            res = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
          } catch (refreshErr) {
            clearTokens();
            setInput(text.trim());
            setMessages(prev => [...prev, {
              role: "ai",
              id: Date.now() + 1,
              isError: true,
              needsLogin: true,
              text: "Your session has expired. Please log in again — your question will be waiting."
            }]);
            return;
          }
        } else {
          throw firstErr;
        }
      }
      const aiMsg = { role: "ai", text: res.data.answer, id: Date.now() + 1 };
      setMessages(prev => [...prev, aiMsg]);
      if (res.data.session_id) setSessionId(res.data.session_id);
      if (!isPremium) {
        const left = bonusQuestions > 0 ? bonusQuestions - 1 : 0;
        setBonusQuestions(left);
        setFreeUsed(left <= 0);
      }
      // Update remaining counter for ai_chat plan
      if (typeof res.data.questions_remaining === "number" && user?.plan === "ai_chat") {
        setQuestionsRemaining(res.data.questions_remaining);
      }
      // Sync user state (so other pages reflect the new count)
      if (refreshUser) refreshUser();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "";
      // 402 from /chat/message when the AI Chat plan's 4 questions are used
      // up — surface BOTH options as clickable CTAs: recharge the AI plan OR
      // book a live astrologer.
      const looksLikeExhausted = status === 402 && (
        detail.toLowerCase().includes("astrologer") ||
        detail.toLowerCase().includes("recharge") ||
        detail.toLowerCase().includes("all") // "used all 4 questions"
      );
      if (status === 428) {
        // Backend Kundli gate — birth details missing
        setChartJson(null);
        setShowBirthForm(true);
        setMessages(prev => [...prev, { role: "ai", id: Date.now() + 1, isChartGate: true, text: t("chart.needDetails") }]);
      } else if (looksLikeExhausted) {
        setQuestionsRemaining(0);
        setMessages(prev => [...prev, {
          role: "ai",
          id: Date.now() + 1,
          isAstrologerCTA: true,
          text: detail || "You've used all your AI questions. Recharge to continue chatting or talk to a live astrologer for deeper guidance."
        }]);
        if (refreshUser) refreshUser();
      } else if (status === 402) {
        // Free question already used or plan required
        setMessages(prev => [...prev, {
          role: "ai",
          id: Date.now() + 1,
          isPaywall: true,
          text: `✨ You've used your free question. Unlock 4 AI questions with the ${price("ai_chat")} plan, or book a human astrologer for just ${price("consultation")} (50% OFF, limited time).`
        }]);
        setShowPaywall(true);
      } else {
        const friendly = typeof detail === "string" && detail.length < 200
          ? detail
          : "The cosmic connection was briefly interrupted. Please try sending your question again.";
        setMessages(prev => [...prev, { role: "ai", text: friendly, id: Date.now() + 1, isError: true }]);
      }
    } finally { setLoading(false); }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  // Back from sign-up with the question they typed as a visitor: send it once.
  useEffect(() => {
    if (autoSentRef.current || !user || chartLoading || !chartJson) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("autosend") !== "1") return;
    const q = (params.get("ask") || "").trim();
    autoSentRef.current = true;
    window.history.replaceState(null, "", "/chat");
    if (!q) return;
    if (user.free_question_used && !(user.bonus_questions > 0) && !["ai_chat", "consultation", "admin"].includes(user.plan)) {
      setInput(q);
      setShowPaywall(true);
      return;
    }
    handleSend(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, chartLoading, chartJson]);

  // Step 1 — no chart yet (or the user asked for a new one): a focused birth-details
  // page. No chat shell, no banner, nothing competing with the form.
  const needsDetails = !authLoading && !chartLoading && !showChooser && (!chartJson || showBirthForm);
  const pendingQuestion = input.trim();

  if (needsDetails) {
    return (
      <div data-testid="chat-details-stage" className="chat-details-root" style={{ minHeight: "100vh", background: "#120021", paddingTop: "84px", paddingBottom: "96px", position: "relative" }}>
        <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
          <div style={{ position: "absolute", width: "520px", height: "520px", background: "radial-gradient(circle, rgba(91,30,140,0.25), transparent)", top: "-120px", right: "-160px", borderRadius: "50%" }} />
          <div style={{ position: "absolute", width: "420px", height: "420px", background: "radial-gradient(circle, rgba(45,11,69,0.35), transparent)", bottom: "-80px", left: "-140px", borderRadius: "50%" }} />
        </div>

        <div style={{ maxWidth: "540px", margin: "0 auto", padding: "0 16px", position: "relative", zIndex: 1 }}>
          <div style={{ textAlign: "center", marginBottom: "18px" }}>
            <span data-testid="chat-step-pill" style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "rgba(255,209,102,0.1)", border: "1px solid rgba(255,209,102,0.22)", borderRadius: "50px", padding: "5px 14px", color: "#FFD166", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.3px" }}>
              <Sparkles size={12} /> {t("chart.step1")}
            </span>
            <h1 className="font-cinzel gradient-text-gold" style={{ fontSize: "clamp(1.4rem, 5vw, 1.9rem)", margin: "12px 0 8px", lineHeight: 1.25 }}>
              {t("chart.stepTitle")}
            </h1>
            <p style={{ color: "rgba(255,255,255,0.58)", fontSize: "0.88rem", lineHeight: 1.6, maxWidth: "420px", margin: "0 auto" }}>
              {t("chart.stepDesc")}
            </p>
          </div>

          {pendingQuestion && (
            <div data-testid="chat-pending-question" style={{ background: "rgba(157,78,221,0.1)", border: "1px solid rgba(157,78,221,0.28)", borderRadius: "12px", padding: "11px 14px", marginBottom: "14px" }}>
              <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "3px" }}>{t("chart.yourQuestion")}</div>
              <div style={{ color: "#e9d5ff", fontSize: "0.86rem", lineHeight: 1.5 }}>&ldquo;{pendingQuestion}&rdquo;</div>
            </div>
          )}

          <InlineBirthForm onConnected={handleChartConnected} defaultName={user?.name || ""}
            title={t("chart.formTitle")} subtitle={t("chart.formSub")} />

          {chartJson && showBirthForm && (
            <button data-testid="details-back-to-chat-btn" onClick={() => setShowBirthForm(false)} className="btn-ghost"
              style={{ width: "100%", marginTop: "12px", padding: "11px", fontSize: "0.84rem" }}>
              ← {t("chart.backToChat")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="chat-page-root" style={{ minHeight: "100vh", background: "#120021", display: "flex", flexDirection: "column", paddingTop: "64px" }}>
      {/* Background */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", width: "500px", height: "500px", background: "radial-gradient(circle, rgba(91,30,140,0.2), transparent)", top: 0, right: 0, borderRadius: "50%" }} />
        <div style={{ position: "absolute", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(45,11,69,0.3), transparent)", bottom: 0, left: 0, borderRadius: "50%" }} />
      </div>

      <div className="flex-col md:flex-row chat-wrap" style={{ display: "flex", flex: 1, maxWidth: "1100px", margin: "0 auto", width: "100%", padding: "24px 16px", gap: "20px", position: "relative", zIndex: 1 }}>
        
        {/* Sidebar */}
        <div style={{ width: "260px", flexShrink: 0 }} className="hidden md:block">
          {chartSummary && <ChartSummaryCard summary={chartSummary} name={reportName} />}
          <div className="glass-card" style={{ padding: "20px", height: "fit-content" }}>
            <h3 className="font-cinzel" style={{ color: "#FFD166", fontSize: "0.9rem", marginBottom: "16px", display: "flex", alignItems: "center", gap: "6px" }}>
              <Sparkles size={14} /> Suggested Questions
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {SUGGESTED_PROMPTS.map((p, i) => (
                <button key={p} data-testid={`suggested-prompt-${i}`} onClick={() => handleSend(p)}
                  style={{ background: "rgba(157,78,221,0.08)", border: "1px solid rgba(157,78,221,0.2)", borderRadius: "10px", padding: "10px 12px", cursor: "pointer", color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", textAlign: "left", transition: "all 0.2s" }}
                  onMouseEnter={e => { e.target.style.borderColor = "rgba(157,78,221,0.5)"; e.target.style.color = "white"; }}
                  onMouseLeave={e => { e.target.style.borderColor = "rgba(157,78,221,0.2)"; e.target.style.color = "rgba(255,255,255,0.7)"; }}>
                  {p}
                </button>
              ))}
            </div>

            {chartJson && (
              <div style={{ marginTop: "20px", padding: "12px", background: "rgba(255,209,102,0.05)", borderRadius: "10px", border: "1px solid rgba(255,209,102,0.1)" }}>
                <p data-testid="chart-connected-label" style={{ color: "#FFD166", fontSize: "0.72rem", fontWeight: "600" }}>
                  ✦ {t("chart.connected")}
                </p>
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", marginTop: "4px" }}>
                  AI uses {reportName ? `${reportName}'s` : "your"} full Kundli for every answer
                </p>
                {charts.length > 1 && (
                  <div style={{ marginTop: "10px" }}>
                    <label style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem", display: "block", marginBottom: "4px" }}>{t("chart.selectLabel")}</label>
                    <select data-testid="chart-selector" value={activeChartId || ""} onChange={e => switchChart(e.target.value)} className="cosmic-input" style={{ fontSize: "0.75rem", padding: "6px 8px" }}>
                      {charts.map(c => (
                        <option key={c.id} value={c.id}>{c.name} · {c.dob}</option>
                      ))}
                    </select>
                  </div>
                )}
                <button data-testid="add-birth-details-btn" onClick={() => setShowBirthForm(true)} style={{ marginTop: "8px", background: "none", border: "none", cursor: "pointer", color: "rgba(255,209,102,0.6)", fontSize: "0.7rem", padding: 0 }}>
                  {t("chart.addAnother")}
                </button>
                {activeChartId && (
                  <button data-testid="chat-download-pdf-btn" disabled={pdfBusy}
                    onClick={async () => {
                      setPdfBusy(true);
                      try { await downloadKundliPdf(activeChartId, reportName, lang); }
                      catch { setMessages(prev => [...prev, { role: "ai", id: Date.now(), isError: true, text: t("glance.pdfFailed") }]); }
                      finally { setPdfBusy(false); }
                    }}
                    className="btn-ghost" style={{ marginTop: "10px", width: "100%", padding: "7px 10px", fontSize: "0.72rem", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "5px", opacity: pdfBusy ? 0.6 : 1 }}>
                    <Download size={12} /> {pdfBusy ? t("glance.preparingPdf") : t("glance.downloadPdf")}
                  </button>
                )}
              </div>
            )}
            {!chartJson && (
              <div style={{ marginTop: "20px", padding: "12px", background: "rgba(157,78,221,0.05)", borderRadius: "10px", border: "1px solid rgba(157,78,221,0.15)" }}>
                <p data-testid="no-chart-label" style={{ color: "#c084fc", fontSize: "0.72rem", fontWeight: "600" }}>{t("chat.noChart")}</p>
                <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem", marginTop: "4px" }}>{t("chart.gateDesc")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile: one calm line — whose chart is loaded plus the two essentials.
            Everything else (nakshatra, dasha) hides behind "Chart details" so the
            answers get the screen. */}
        <div className="md:hidden chat-mobile-head" data-testid="chat-mobile-panel" style={{ width: "100%" }}>
          {hasAnswer ? null : chartJson ? (
            <div className="glass-card" style={{ padding: "8px 10px", marginBottom: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "space-between" }}>
                <span data-testid="chat-mobile-chart-label" style={{ color: "#FFD166", fontSize: "0.76rem", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                  ✦ {reportName || "Your Kundli"}
                  {chartSummary?.lagna && <span style={{ color: "rgba(255,255,255,0.45)", fontWeight: 500 }}> · {chartSummary.lagna}</span>}
                </span>
                <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0 }}>
                  {!isPremium && !freeUsed && (
                    <span data-testid="chat-mobile-free-badge" style={{ background: "rgba(255,209,102,0.12)", border: "1px solid rgba(255,209,102,0.3)", borderRadius: "50px", padding: "3px 9px", color: "#FFD166", fontSize: "0.66rem", fontWeight: 700 }}>
                      1 FREE
                    </span>
                  )}
                  {user?.plan === "ai_chat" && questionsRemaining !== null && (
                    <span data-testid="chat-mobile-remaining-badge" style={{ background: "rgba(157,78,221,0.14)", border: "1px solid rgba(157,78,221,0.3)", borderRadius: "50px", padding: "3px 9px", color: "#c084fc", fontSize: "0.66rem", fontWeight: 700 }}>
                      {questionsRemaining}/4
                    </span>
                  )}
                  {chartSummary && (
                    <button data-testid="chat-mobile-glance-toggle" onClick={() => setShowGlance(v => !v)} className="btn-ghost"
                      style={{ padding: "5px 9px", fontSize: "0.7rem" }}>{showGlance ? "×" : t("chart.detailsToggle")}</button>
                  )}
                  <button data-testid="chat-mobile-add-chart-btn" onClick={() => setShowBirthForm(true)} className="btn-ghost"
                    style={{ padding: "5px 9px", fontSize: "0.7rem" }}>+ New</button>
                </div>
              </div>
              {showGlance && (
                <div data-testid="chat-mobile-glance" style={{ marginTop: "8px" }}>
                  <ChartSummaryCard summary={chartSummary} variant="strip" />
                  {activeChartId && <KundliPdfActions reportId={activeChartId} name={reportName} compact testPrefix="chat-mobile" />}
                  {charts.length > 1 && (
                    <select data-testid="chat-mobile-chart-selector" value={activeChartId || ""} onChange={e => switchChart(e.target.value)}
                      className="cosmic-input" style={{ marginTop: "8px", fontSize: "0.8rem", padding: "8px 10px", width: "100%" }}>
                      {charts.map(c => <option key={c.id} value={c.id}>{c.name} · {c.dob}</option>)}
                    </select>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Chat area */}
        <div className="chat-col" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "calc(100vh - 140px)" }}>
          {/* Header */}
          <div className={`glass-card chat-header${chartJson && !hasAnswer ? " chat-header-dup" : ""}`} style={{ padding: "16px 20px", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Star size={20} style={{ color: "#FFD166" }} fill="#FFD166" />
                <div>
                  <div className="font-cinzel" style={{ color: "#FFD166", fontSize: "1rem" }}>AstroFate AI</div>
                  <div className="chat-header-sub" style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem" }}>
                    {hasAnswer && chartJson ? `✦ ${reportName ? `${reportName}'s Kundli` : "Your Kundli"}` : chartJson ? "Vedic Chart Analysis Active" : "Vedic Astrology Expert"}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {chartJson && (
                  <span className="chart-badge-desktop" style={{ background: "rgba(255,209,102,0.1)", border: "1px solid rgba(255,209,102,0.2)", borderRadius: "50px", padding: "3px 10px", color: "#FFD166", fontSize: "0.7rem" }}>
                    Chart ✦
                  </span>
                )}
                {!isPremium && !freeUsed && (
                  <span data-testid="free-question-badge" style={{ background: "rgba(255,209,102,0.1)", border: "1px solid rgba(255,209,102,0.3)", borderRadius: "50px", padding: "4px 12px", color: "#FFD166", fontSize: "0.75rem", fontWeight: "600" }}>
                    {bonusQuestions > 0 ? `${bonusQuestions} Earned Question${bonusQuestions > 1 ? "s" : ""}` : "1 Free Question"}
                  </span>
                )}
                {user?.plan === "ai_chat" && questionsRemaining !== null && (
                  <span data-testid="questions-remaining-badge" style={{ background: questionsRemaining === 0 ? "rgba(239,68,68,0.1)" : "rgba(157,78,221,0.1)", border: `1px solid ${questionsRemaining === 0 ? "rgba(239,68,68,0.3)" : "rgba(157,78,221,0.3)"}`, borderRadius: "50px", padding: "4px 12px", color: questionsRemaining === 0 ? "#fca5a5" : "#c084fc", fontSize: "0.75rem", fontWeight: "600" }}>
                    {questionsRemaining}/4 questions left
                  </span>
                )}
                {(user?.plan === "consultation" || user?.role === "admin") && (
                  <span style={{ background: "rgba(157,78,221,0.1)", border: "1px solid rgba(157,78,221,0.3)", borderRadius: "50px", padding: "4px 12px", color: "#c084fc", fontSize: "0.75rem", fontWeight: "600" }}>
                    {user?.role === "admin" ? "Admin" : "Consultation Plan"}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Messages */}
          <div data-testid="chat-messages" className="chat-scroll" style={{ flex: 1, overflowY: "auto", padding: "4px 0", marginBottom: "16px", display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* Multiple-Kundli chooser — ask which chart to read before answering */}
            {showChooser && (
              <div data-testid="chart-chooser" className="glass-card animate-fadeInUp" style={{ padding: "18px 20px", border: "1px solid rgba(255,209,102,0.35)", background: "linear-gradient(135deg, rgba(255,209,102,0.1), rgba(157,78,221,0.06))" }}>
                <p className="font-cinzel" style={{ color: "#FFD166", fontSize: "0.98rem", fontWeight: 700, marginBottom: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <Sparkles size={15} /> {t("chart.chooseTitle")}
                </p>
                <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem", marginBottom: "14px" }}>{t("chart.choosePrompt")}</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {charts.map((c, i) => (
                    <button key={c.id} type="button" data-testid={`chart-choice-${i}`} onClick={() => switchChart(c.id)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", padding: "12px 14px", borderRadius: "12px", cursor: "pointer", textAlign: "left",
                        border: "1px solid rgba(157,78,221,0.3)", background: "rgba(157,78,221,0.08)", color: "white", transition: "all 0.2s" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,209,102,0.6)"; e.currentTarget.style.background = "rgba(255,209,102,0.08)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(157,78,221,0.3)"; e.currentTarget.style.background = "rgba(157,78,221,0.08)"; }}>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>{c.name || "Kundli"}</span>
                        <span style={{ display: "block", color: "rgba(255,255,255,0.5)", fontSize: "0.76rem" }}>{c.dob}</span>
                      </span>
                      <span style={{ color: "#FFD166", fontSize: "0.8rem", fontWeight: 700, flexShrink: 0 }}>{t("chart.useThis")} →</span>
                    </button>
                  ))}
                </div>
                <button data-testid="chooser-new-kundli-btn" onClick={() => { setChartChosen(true); setShowBirthForm(true); }}
                  className="btn-ghost" style={{ marginTop: "12px", width: "100%", padding: "9px", fontSize: "0.8rem" }}>
                  + Create a new Kundli instead
                </button>
              </div>
            )}
            {messages.length === 0 && !showChooser && (
              <div style={{ textAlign: "center", padding: "28px 8px" }}>
                <div style={{ fontSize: "2.4rem", marginBottom: "12px", opacity: 0.4 }}>◎</div>
                <h3 className="font-cinzel" style={{ color: "#FFD166", marginBottom: "8px" }}>Ask the Stars</h3>
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.88rem" }}>
                  {!user ? (chartJson ? t("chat.guestReady") : t("chat.guestStart"))
                    : !chartJson ? t("chart.gateDesc")
                    : (freeUsed && !isPremium) ? "Upgrade to continue chatting"
                    : isAiChatExhausted ? "Book an astrologer for deeper guidance"
                    : isPremium ? "Ask anything — answers come from your chart"
                    : "You have 1 free question remaining"}
                </p>
                {user && freeUsed && !isPremium && (
                  <button data-testid="chat-empty-invite-link" onClick={() => navigate("/dashboard")} style={{
                    background: "none", border: "none", cursor: "pointer", marginTop: "10px",
                    color: "rgba(255,209,102,0.85)", fontSize: "0.82rem", fontWeight: 600,
                    textDecoration: "underline", textUnderlineOffset: "3px",
                  }}>
                    Or invite a friend and earn a free question
                  </button>
                )}
                {!user && chartJson && hasFullReport && (                  <button data-testid="guest-view-report-btn" onClick={() => navigate("/kundli/report")} className="btn-ghost" style={{ marginTop: "14px", padding: "8px 18px", fontSize: "0.82rem" }}>
                    {t("chat.viewFullReport")}
                  </button>
                )}
                {user && freeUsed && !isPremium && (
                  <button data-testid="upgrade-btn" onClick={() => setShowPaywall(true)} className="btn-gold" style={{ marginTop: "16px" }}>
                    Upgrade — {price("ai_chat")}/24hrs
                  </button>
                )}
                {/* Mobile: tap-and-go question ideas (the desktop sidebar is hidden) */}
                <div className="md:hidden chat-prompt-chips" data-testid="chat-mobile-prompts">
                  {SUGGESTED_PROMPTS.map((p, i) => (
                    <button key={p} data-testid={`mobile-suggested-prompt-${i}`} onClick={() => handleSend(p)}
                      style={{ background: "rgba(157,78,221,0.12)", border: "1px solid rgba(157,78,221,0.28)", borderRadius: "999px", padding: "7px 12px", color: "rgba(255,255,255,0.75)", fontSize: "0.74rem", cursor: "pointer" }}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, mi) => (
              <div key={msg.id} ref={mi === messages.length - 1 ? lastMsgRef : undefined}
                style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", scrollMarginTop: "4px" }}>
                {msg.role === "ai" && (
                  <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "linear-gradient(135deg, #9D4EDD, #5B1E8C)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginRight: "10px", alignSelf: "flex-end" }}>
                    <Star size={14} fill="white" style={{ color: "white" }} />
                  </div>
                )}
                <div data-testid={`message-${msg.role}`} className={msg.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"}
                  style={{ maxWidth: msg.role === "ai" ? "88%" : "75%", padding: "12px 16px", fontSize: "0.9rem", lineHeight: 1.7, color: msg.isError ? "#fca5a5" : (msg.isAstrologerCTA ? "#FFD166" : "rgba(255,255,255,0.9)"), whiteSpace: msg.role === "user" ? "pre-wrap" : "normal", border: msg.isAstrologerCTA ? "1px solid rgba(255,209,102,0.3)" : undefined, background: msg.isAstrologerCTA ? "rgba(255,209,102,0.05)" : undefined }}>
                  {msg.role === "ai" && !msg.isError && !msg.isAstrologerCTA && !msg.isPaywall
                    ? <AstroMarkdown>{msg.text}</AstroMarkdown>
                    : msg.text}
                  {msg.needsLogin && (
                    <div style={{ marginTop: "12px" }}>
                      <button data-testid="msg-login-btn" onClick={() => navigate("/auth?tab=login&redirect=/chat")} className="btn-gold" style={{ padding: "6px 14px", fontSize: "0.78rem" }}>
                        Log in to continue
                      </button>
                    </div>
                  )}
                  {msg.isTeaser && (
                    <div data-testid="chat-teaser-footer" style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid rgba(255,209,102,0.18)", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      <span style={{ color: "#FFD166", fontSize: "0.74rem", fontWeight: 700 }}>✦ {t("chart.firstRead")}</span>
                      <AccuracyVote kind="chat" reportId={activeChartId} compact />
                    </div>
                  )}
                  {msg.isDeepRead && (
                    <div data-testid="chat-deep-read-footer" style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid rgba(192,132,252,0.22)", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      <span style={{ color: "#c084fc", fontSize: "0.74rem", fontWeight: 700 }}>
                        ✦ {lang === "hi" ? "आपका स्वभाव और हाल के महीने" : "Your nature, and these recent months"}
                      </span>
                    </div>
                  )}
                  {msg.isChartConnected && (
                    <div style={{ marginTop: "12px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <button data-testid="msg-start-chat-btn" onClick={() => inputRef.current?.focus()} className="btn-gold" style={{ padding: "8px 16px", fontSize: "0.8rem" }}>
                        {t("chart.startChat")} →
                      </button>
                      {hasFullReport && (
                        <button data-testid="msg-view-report-btn" onClick={() => navigate("/kundli/report")} className="btn-ghost" style={{ padding: "8px 14px", fontSize: "0.8rem" }}>
                          {t("chat.viewFullReport")}
                        </button>
                      )}
                    </div>
                  )}
                  {msg.isAstrologerCTA && (
                    <div style={{ marginTop: "12px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <button data-testid="msg-recharge-btn" onClick={() => setShowPaywall(true)} className="btn-gold" style={{ padding: "6px 14px", fontSize: "0.78rem" }}>
                        {price("ai_chat")} — Recharge AI Chat
                      </button>
                      <button data-testid="msg-astrologer-btn" onClick={() => navigate("/consultation")} className="btn-purple" style={{ padding: "6px 14px", fontSize: "0.78rem" }}>
                        {price("consultation")} — Talk to Astrologer (50% OFF)
                      </button>
                    </div>
                  )}
                  {msg.isPaywall && (
                    <div style={{ marginTop: "12px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <button data-testid="msg-paywall-ai-btn" onClick={() => setShowPaywall(true)} className="btn-gold" style={{ padding: "6px 14px", fontSize: "0.78rem" }}>
                        {price("ai_chat")} — Unlock 4 Questions
                      </button>
                      <button data-testid="msg-paywall-consult-btn" onClick={() => navigate("/consultation")} className="btn-purple" style={{ padding: "6px 14px", fontSize: "0.78rem" }}>
                        {price("consultation")} — Talk to Astrologer (50% OFF)
                      </button>
                    </div>
                  )}
                </div>
                {msg.role === "user" && (
                  <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "linear-gradient(135deg, #F4B942, #FFD166)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: "10px", alignSelf: "flex-end", color: "#120021", fontWeight: "700", fontSize: "0.8rem" }}>
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </div>
                )}
              </div>
            ))}
    {loading && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "linear-gradient(135deg, #9D4EDD, #5B1E8C)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Star size={14} fill="white" style={{ color: "white" }} />
                </div>
                <div className="chat-bubble-ai">
                  <TypingIndicator />
                  <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.7rem", paddingLeft: "4px", marginTop: "2px" }}>
                    {chartJson ? "Consulting your birth chart..." : "Consulting the stars..."}
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Paywall card: appears after the free question is used */}
          {freeUsed && !isPremium && messages.length > 0 && (
            <div className="paywall-bar" style={{ background: "linear-gradient(to bottom, transparent, rgba(18,0,33,0.98))", padding: "24px", textAlign: "center", borderRadius: "16px", border: "1px solid rgba(255,209,102,0.2)", marginBottom: "16px" }}>
              <h3 className="font-cinzel paywall-bar-title" style={{ color: "#FFD166", marginBottom: "8px" }}>Continue Your Reading</h3>
              <p className="paywall-bar-sub" style={{ color: "rgba(255,255,255,0.5)", marginBottom: "16px", fontSize: "0.88rem" }}>Unlock 4 AI questions for {price("ai_chat")} or talk to a human astrologer</p>
              <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                <button data-testid="unlock-ai-btn" onClick={() => setShowPaywall(true)} className="btn-gold">
                  {price("ai_chat")} — <span className="cta-full">Unlock 4 Questions</span><span className="cta-mini">4 Questions</span>
                </button>
                <button data-testid="book-consultation-btn" onClick={() => navigate("/consultation")} className="btn-purple">
                  {price("consultation")} — <span className="cta-full">Talk to Astrologer </span><span className="cta-mini">Astrologer</span>
                  <span className="cta-full" style={{ textDecoration: "line-through", opacity: 0.6, fontSize: "0.8em" }}>{priceOld("consultation")}</span>
                </button>
              </div>
              <button data-testid="chat-invite-friend-link" onClick={() => navigate("/dashboard")} style={{
                background: "none", border: "none", cursor: "pointer", marginTop: "12px",
                color: "rgba(255,209,102,0.85)", fontSize: "0.8rem", fontWeight: 600,
                textDecoration: "underline", textUnderlineOffset: "3px",
              }}>
                Or invite a friend and earn a free question
              </button>
            </div>
          )}

          {/* Astrologer upsell: appears when AI Chat plan's 4 questions are exhausted */}
          {isAiChatExhausted && messages.length > 0 && (
            <div data-testid="astrologer-cta-card" className="paywall-bar" style={{ background: "linear-gradient(to bottom, transparent, rgba(18,0,33,0.98))", padding: "24px", textAlign: "center", borderRadius: "16px", border: "1px solid rgba(157,78,221,0.3)", marginBottom: "16px" }}>
              <h3 className="font-cinzel paywall-bar-title" style={{ color: "#c084fc", marginBottom: "8px" }}>Want Deeper Guidance?</h3>
              <p className="paywall-bar-sub" style={{ color: "rgba(255,255,255,0.5)", marginBottom: "16px", fontSize: "0.88rem" }}>You&apos;ve used your 4 AI questions. Book a 15-minute session with a certified Vedic astrologer — 50% OFF for a limited time.</p>
              <button data-testid="astrologer-card-btn" onClick={() => navigate("/consultation")} className="btn-purple">
                {price("consultation")} — <span className="cta-full">Talk to Astrologer </span><span className="cta-mini">Astrologer</span>
                <span className="cta-full" style={{ textDecoration: "line-through", opacity: 0.6, fontSize: "0.8em" }}>{priceOld("consultation")}</span>
              </button>
            </div>
          )}

          {/* Input or Login-gate */}
          <div className="chat-composer">
          {authLoading ? null : showChooser ? (
            <div data-testid="chooser-hint" className="glass-card" style={{ padding: "14px 16px", textAlign: "center", border: "1px dashed rgba(255,209,102,0.35)" }}>
              <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.84rem" }}>
                Pick a Kundli above to start asking questions
              </span>
            </div>
          ) : (!chartJson || showBirthForm) ? null : (
            <div>
              {!user && (
                <div data-testid="guest-signup-hint" style={{ display: "flex", alignItems: "center", gap: "8px", padding: "0 6px 8px", color: "rgba(255,209,102,0.85)", fontSize: "0.76rem" }}>
                  <Sparkles size={12} /> {t("chat.guestHint")}
                </div>
              )}
            <div className="glass-card" style={{ padding: "12px 16px", display: "flex", gap: "10px", alignItems: "flex-end" }}>
              <textarea
                data-testid="chat-input"
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  (freeUsed && !isPremium) ? "Upgrade to continue chatting..."
                  : isAiChatExhausted ? "Book an astrologer to continue..."
                  : !user ? t("chat.guestPlaceholder")
                  : "Ask about your destiny..."
                }
                disabled={(freeUsed && !isPremium) || isAiChatExhausted || loading}
                rows={1}
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "white", fontFamily: "'DM Sans', sans-serif", fontSize: "0.95rem", resize: "none", lineHeight: 1.5, maxHeight: "100px", overflowY: "auto" }}
              />
              <button
                data-testid="chat-send-btn"
                onClick={() => handleSend()}
                disabled={!input.trim() || loading || (freeUsed && !isPremium) || isAiChatExhausted}
                className="btn-gold"
                style={{ padding: "10px 16px", flexShrink: 0, opacity: (!input.trim() || loading || (freeUsed && !isPremium) || isAiChatExhausted) ? 0.5 : 1 }}>
                <Send size={16} />
              </button>
            </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} onSuccess={(plan) => { setFreeUsed(false); }} />}
    </div>
  );
}
